import React, { useRef } from 'react'
import styled from '@emotion/styled'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'next-i18next'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from '../../../order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import FlowOneColumnStepLayout from '../one-column-step-layout/one-column-step-layout'
import OrderCustomQuestionField from 'modules/order/components/custom-questions/custom-question-field'
import { OrderCustomAnswersSlice } from 'modules/order/state/custom-answers/custom-answers-types'
import { DEFAULT_THEME_COLORS } from '../../../../css/theme'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
  trackFormSkipEvent,
} from '../../../analytics/helpers/track-form-events'

import useTrackUIElementEvent from '../../../analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'

const StyledForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`

type FormData = OrderCustomAnswersSlice

const OptionalCustomQuestionStep = () => {
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const { t } = useTranslation()

  const trackUIElementEvent = useTrackUIElementEvent()

  const primaryColor =
    config.theme['--theme-color__brand-primary'] ||
    DEFAULT_THEME_COLORS['--theme-color--neutral-10']

  const form = useForm<FormData>({
    values: { ...order.customAnswers.value },
    resolver: yupResolver(order.customAnswers.schema),
  })

  // Track the last error to avoid sending multiple of the same error
  const lastTrackedErrorRef = useRef<string | null>(null)

  const hasError = !!Object.keys(form.formState.errors).length
  const questionId = Number(flow.currentStep.name.split('-')[1])
  const questionConfig = config.questions.custom.find((question) => question.id === questionId)

  const goNext = () => {
    if (hasError) {
      // Track form error if it's different from the last tracked error
      const currentError = JSON.stringify(form.formState.errors)

      if (lastTrackedErrorRef.current !== currentError) {
        trackFormErrorEvent(flow.currentStep.name, currentError)

        lastTrackedErrorRef.current = currentError
      }

      return
    }

    const nextStep = flow.nextStep

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }

  const onSubmit = (data: FormData) => {
    trackFormSubmitEvent(flow.currentStep.name)

    order.customAnswers.save(data as Record<string, string>)
    order.customAnswers.setValue(data as Record<string, string>)

    trackUIElementEvent('Button Clicked', mParticleSdk.EventType.Other, {
      tracking_id: 'custom question submitted',
      text: t('RequiredCustomQuestionStep.footer.continueButton', 'Continue'),
      link_url: window.location.href,
      outbound: false,
    })

    goNext()
  }

  const onSkip = () => {
    trackFormSkipEvent(flow.currentStep.name)

    goNext()
  }

  return (
    <FormProvider {...form}>
      <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
        <>
          <FlowOneColumnStepLayout.Main
            title=""
            hideBackButton={hasError}
            backButtonDataId="custom-question-post-conversion-step-back-button"
          >
            <Wrapper>
              {questionConfig && <OrderCustomQuestionField question={questionConfig} />}
            </Wrapper>
          </FlowOneColumnStepLayout.Main>
          <FlowOneColumnStepLayout.Footer>
            <FlowOneColumnStepLayout.FooterButton
              type="button"
              onClick={onSkip}
              color={DEFAULT_THEME_COLORS['--theme-color--neutral-100']}
              borderColor={DEFAULT_THEME_COLORS['--theme-color--neutral-80']}
              dataId="custom-question-post-conversion-step-skip-button"
            >
              {t('OptionalCustomQuestionStep.skipButton', 'Skip')}
            </FlowOneColumnStepLayout.FooterButton>
            <FlowOneColumnStepLayout.FooterButton
              type="submit"
              color={primaryColor}
              dataId="custom-question-post-conversion-step-continue-button"
            >
              {t('OptionalCustomQuestionStep.continueButton', 'Continue')}
            </FlowOneColumnStepLayout.FooterButton>
          </FlowOneColumnStepLayout.Footer>
        </>
      </StyledForm>
    </FormProvider>
  )
}

export default OptionalCustomQuestionStep

const Wrapper = styled.div`
  flex: 1;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  > div {
    width: 100%;
    max-width: 400px;
  }
`
